import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CreditsProvider } from './contexts/CreditsContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import PrivateRoute from './components/PrivateRoute';
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import BuyersList from './components/BuyersList';
import TestPage from './components/TestPage';
import SearchPage from './pages/SearchPage';
import CampaignsPage from './pages/CampaignsPage';
import CampaignDetailPage from './pages/CampaignDetailPage';
import TemplatesPage from './pages/TemplatesPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ABTestingPage from './pages/ABTestingPage';
import PerformancePage from './pages/PerformancePage';
import SettingsPage from './pages/SettingsPage';
import AutoPilotPage from './pages/AutoPilotPage';
import BillingPage from './pages/BillingPage';
import InboxPage from './pages/InboxPage';
import AutomationPage from './pages/AutomationPage';
import RoleRoute from './components/RoleRoute';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminSearchPage from './pages/admin/AdminSearchPage';
import AdminTables from './pages/admin/AdminTables';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import UnsubscribePage from './pages/UnsubscribePage';
import GettingStartedPage from './pages/GettingStartedPage';
import './App.css';

// 404 Not Found component
const NotFound = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    textAlign: 'center',
    padding: '40px 20px',
    backgroundColor: 'var(--bg-color)'
  }}>
    <h1 style={{ fontSize: '48px', color: 'var(--text-primary)', marginBottom: '16px' }}>
      404
    </h1>
    <h2 style={{ fontSize: '24px', color: 'var(--text-body)', marginBottom: '16px' }}>
      Page Not Found
    </h2>
    <p style={{ color: 'var(--text-secondary)', marginBottom: '32px', maxWidth: '400px' }}>
      The page you're looking for doesn't exist or has been moved.
    </p>
    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
      <button
        onClick={() => window.location.href = '/'}
        style={{
          backgroundColor: 'var(--primary-color)',
          color: 'white',
          border: 'none',
          padding: '12px 24px',
          borderRadius: '6px',
          fontSize: '16px',
          cursor: 'pointer'
        }}
      >
        Go to Dashboard
      </button>
      <button
        onClick={() => window.history.back()}
        style={{
          backgroundColor: 'var(--surface-strong)',
          color: 'var(--text-primary)',
          border: 'none',
          padding: '12px 24px',
          borderRadius: '6px',
          fontSize: '16px',
          cursor: 'pointer'
        }}
      >
        Go Back
      </button>
    </div>
  </div>
);

// Toasts in the shared look: white card, hairline border, soft shadow, status-coloured icons.
// Durations are react-hot-toast's defaults (unchanged).
const TOAST_OPTIONS = {
  style: {
    background: '#ffffff',
    color: '#16141f',
    border: '1px solid #e9e8f1',
    borderRadius: '12px',
    boxShadow: '0 1px 2px rgba(18, 15, 40, 0.05), 0 8px 24px rgba(18, 15, 40, 0.10)',
    padding: '10px 14px',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.45,
    maxWidth: '360px'
  },
  success: {
    iconTheme: { primary: '#15803d', secondary: '#ffffff' }
  },
  error: {
    iconTheme: { primary: '#dc2626', secondary: '#ffffff' }
  }
};

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <Toaster position="top-right" toastOptions={TOAST_OPTIONS} />
        <AuthProvider>
          <CreditsProvider>
          <ErrorBoundary>
            <Routes>
              {/* Public marketing routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Protected app routes — pathless layout route keeps all URLs as-is
                  e.g. /dashboard, /buyers, /billing etc. No URL prefix change. */}
              <Route element={
                <PrivateRoute>
                  <OnboardingProvider>
                    <Layout />
                  </OnboardingProvider>
                </PrivateRoute>
              }>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/getting-started" element={<GettingStartedPage />} />
                <Route path="/autopilot" element={<AutoPilotPage />} />
                <Route path="/buyers" element={<BuyersList />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/campaigns" element={<CampaignsPage />} />
                <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/ab-testing" element={<ABTestingPage />} />
                <Route path="/performance" element={<PerformancePage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/inbox" element={<InboxPage />} />
                <Route path="/automation" element={<AutomationPage />} />
                <Route path="/test" element={<TestPage />} />
              </Route>

              {/* Super admin operator area (separate from the customer app) */}
              <Route element={
                <RoleRoute allow="super_admin">
                  <AdminLayout />
                </RoleRoute>
              }>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/customers" element={<AdminCustomers />} />
                <Route path="/admin/search" element={<AdminSearchPage />} />
                <Route path="/admin/tables" element={<AdminTables />} />
                <Route path="/admin/audit" element={<AdminAuditLog />} />
              </Route>

              {/* 404 page */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </ErrorBoundary>
          </CreditsProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
